import { useState, useEffect, useRef, useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Search, LogOut, Upload, RefreshCw, Edit, Filter, Download, Star, AlertCircle, Ship, TrendingUp, TrendingDown, ArrowUpDown, ShoppingCart, CheckCircle, Clock, Share2, Mic, MicOff, Copy, Layers, WifiOff, ChevronUp, X, BarChart3, ChevronDown, Sparkles, ClipboardList, MoreHorizontal, HelpCircle } from "lucide-react";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const AI_EXCLUDED_CATEGORIES = new Set(["Uncategorized", "Discounted"]);
// Display name mapping for category pills
const CATEGORY_DISPLAY = { "Uncategorized": "Other Products" };
const SECONDARY_CATEGORIES = new Set(["Uncategorized", "Discounted"]);
// AI insights only analyze SQM products (excludes Nos, LM, KG, etc.)
const isAIEligible = (stock) =>
  !AI_EXCLUDED_CATEGORIES.has(stock.category) &&
  (stock.unit || "").toUpperCase() === "SQM";

// CHANGE 1 — Qty display helper: strips long float tails
function formatQty(v) {
  const n = Number(v ?? 0);
  if (!Number.isFinite(n)) return "0";
  const s = n.toFixed(2);
  return s.endsWith(".00") ? String(Math.round(n)) : s.replace(/(\.\d)0$/, "$1");
}

// Dubai timezone formatter
const dubaiTime = new Intl.DateTimeFormat('en-AE', {
  timeZone: 'Asia/Dubai',
  weekday: 'short',
  day: 'numeric',
  month: 'short',
  hour: '2-digit',
  minute: '2-digit',
  hour12: true
});

export default function StockViewPage({ userRole, user, onLogout }) {
  const navigate = useNavigate();
  const [stocks, setStocks] = useState([]);
  const [allStocks, setAllStocks] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedCategory, setSelectedCategory] = useState(() => {
    if (userRole === "staff") {
      const last = localStorage.getItem('rm_last_category');
      return last || "Carpet Tiles";
    }
    return "All";
  });
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [favorites, setFavorites] = useState(() => {
    const saved = localStorage.getItem('redmagma_favorites');
    return saved ? JSON.parse(saved) : [];
  });
  const [sortBy, setSortBy] = useState("name");
  const [showInStockOnly, setShowInStockOnly] = useState(false);
  const [currentTime, setCurrentTime] = useState(new Date());
  const [lastSynced, setLastSynced] = useState(null);
  const [expandedBatches, setExpandedBatches] = useState({});
  const [isListening, setIsListening] = useState(false);
  // Phase 4: Pipeline freshness
  const [pipelineStatus, setPipelineStatus] = useState(null);
  
  // Pull-to-refresh (staff only)
  const [pullDistance, setPullDistance] = useState(0);
  const [isPulling, setIsPulling] = useState(false);
  const touchStartY = useRef(0);
  const mainRef = useRef(null);
  const PTR_THRESHOLD = 60;

  // Feature pack state
  const [isOffline, setIsOffline] = useState(!navigator.onLine);
  const [syncAgo, setSyncAgo] = useState('');
  const [recentlyViewed, setRecentlyViewed] = useState(() => {
    try { return JSON.parse(localStorage.getItem('rm_recent_v1')) || []; } catch { return []; }
  });
  const [showRecent, setShowRecent] = useState(false);
  const [showQuickJump, setShowQuickJump] = useState(false);
  const [opsSignals, setOpsSignals] = useState(null);
  const [showIntel, setShowIntel] = useState(false);
  const [appReservedSummary, setAppReservedSummary] = useState({});
  const [showNavMenu, setShowNavMenu] = useState(false);
  const [showAdminWidgets, setShowAdminWidgets] = useState(false);
  const [showCategoryLegend, setShowCategoryLegend] = useState(false);
  const searchTimerRef = useRef(null);
  const lastTabRefresh = useRef(0);
  const sessionTimer = useRef(null);
  const STALE_AMBER_MIN = 15;
  const STALE_RED_MIN = 30;
  const SESSION_TIMEOUT_MS = 30 * 60 * 1000;
  
  const REORDER_THRESHOLD = 200; // SQM threshold for reorder suggestions

  // Phase 1A: Health badge thresholds
  const HEALTH_CRITICAL_RATIO = 0.2;
  const HEALTH_WATCH_PRESSURE = 0.70;
  const HEALTH_VERY_LOW_QTY = 10;
  const HEALTH_LOW_QTY = 100;
  
  const toggleBatchExpand = (productCode) => {
    setExpandedBatches(prev => ({
      ...prev,
      [productCode]: !prev[productCode]
    }));
  };

  // Voice Search
  const startVoiceSearch = () => {
    if (!('webkitSpeechRecognition' in window) && !('SpeechRecognition' in window)) {
      toast.error("Voice search not supported in this browser");
      return;
    }
    
    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
    const recognition = new SpeechRecognition();
    
    recognition.lang = 'en-US';
    recognition.continuous = false;
    recognition.interimResults = false;
    
    recognition.onstart = () => {
      setIsListening(true);
      toast.info("🎤 Listening... Say a product name");
    };
    
    recognition.onresult = (event) => {
      const transcript = event.results[0][0].transcript;
      setSearchQuery(transcript);
      toast.success(`Searching for: "${transcript}"`);
    };
    
    recognition.onerror = (event) => {
      setIsListening(false);
      if (event.error === 'no-speech') {
        toast.error("No speech detected. Try again.");
      } else {
        toast.error("Voice search error. Try again.");
      }
    };
    
    recognition.onend = () => {
      setIsListening(false);
    };
    
    recognition.start();
  };

  // Update clock every minute
  useEffect(() => {
    const timer = setInterval(() => setCurrentTime(new Date()), 60000);
    return () => clearInterval(timer);
  }, []);

  // Phase 4: Fetch pipeline status periodically for stale banner
  // Also auto-refreshes stock data when a new ERP sync cycle is detected (#2 push-equivalent)
  const lastSnapshotVersion = useRef(null);
  useEffect(() => {
    const fetchPipelineStatus = async () => {
      try {
        const res = await axios.get(`${API}/sync/pipeline-status`);
        const data = res.data;
        setPipelineStatus(data);

        // Auto-refresh: detect when a new sync cycle has run (version_id changed)
        const newVersion = data?.active_snapshot?.version_id;
        if (newVersion && lastSnapshotVersion.current && newVersion !== lastSnapshotVersion.current) {
          // New ERP sync detected — reload stock silently
          try {
            const stockRes = await axios.get(`${API}/stock`, { withCredentials: true });
            const items = Array.isArray(stockRes.data) ? stockRes.data : stockRes.data?.items || stockRes.data?.stock_items || [];
            setAllStocks(items);
            setLastSynced(new Date());
            toast.success(`Stock updated — ${items.length} items refreshed`, { duration: 3000, id: "auto-sync" });
          } catch { /* non-fatal */ }
        }
        if (newVersion) lastSnapshotVersion.current = newVersion;
      } catch (e) {
        // non-fatal
      }
    };
    fetchPipelineStatus();
    const timer = setInterval(fetchPipelineStatus, 60000); // every 60s
    return () => clearInterval(timer);
  }, []);

  const isAdmin = userRole === "admin";

  // Landing AI highlights + pulse data
  useEffect(() => {
    if (!isAdmin) return;
    const fetchIntel = async () => {
      try {
        const ops = await axios.get(`${API}/ai/ops/signals`);
        setOpsSignals(ops.data);
      } catch (e) { /* non-fatal — admin intel is supplementary */ }
    };
    fetchIntel();
  }, [isAdmin]);

  // Pull-to-refresh touch handlers (staff only)
  const onTouchStart = useCallback((e) => {
    if (isAdmin || refreshing) return;
    touchStartY.current = e.touches[0].clientY;
  }, [isAdmin, refreshing]);

  const onTouchMove = useCallback((e) => {
    if (isAdmin || refreshing) return;
    if (window.scrollY > 0) { setPullDistance(0); setIsPulling(false); return; }
    const delta = e.touches[0].clientY - touchStartY.current;
    if (delta > 0) {
      setPullDistance(Math.min(delta, PTR_THRESHOLD * 2));
      setIsPulling(delta > PTR_THRESHOLD);
    }
  }, [isAdmin, refreshing]);

  const onTouchEnd = useCallback(() => {
    if (isAdmin || refreshing) { setPullDistance(0); setIsPulling(false); return; }
    if (isPulling && pullDistance >= PTR_THRESHOLD) {
      handleRefresh();
    }
    setPullDistance(0);
    setIsPulling(false);
  }, [isAdmin, refreshing, isPulling, pullDistance]);

  useEffect(() => {
    if (isAdmin) return;
    const el = mainRef.current;
    if (!el) return;
    el.addEventListener('touchstart', onTouchStart, { passive: true });
    el.addEventListener('touchmove', onTouchMove, { passive: true });
    el.addEventListener('touchend', onTouchEnd, { passive: true });
    return () => {
      el.removeEventListener('touchstart', onTouchStart);
      el.removeEventListener('touchmove', onTouchMove);
      el.removeEventListener('touchend', onTouchEnd);
    };
  }, [isAdmin, onTouchStart, onTouchMove, onTouchEnd]);

  // Cleanup debounce timer on unmount
  useEffect(() => {
    return () => {
      if (searchTimerRef.current) clearTimeout(searchTimerRef.current);
    };
  }, []);

  // Offline/Online detection
  useEffect(() => {
    const goOffline = () => setIsOffline(true);
    const goOnline = () => { setIsOffline(false); handleRefresh(); };
    window.addEventListener('offline', goOffline);
    window.addEventListener('online', goOnline);
    return () => { window.removeEventListener('offline', goOffline); window.removeEventListener('online', goOnline); };
  }, []);

  // Auto-refresh on tab return (staff only, throttled 2 min)
  useEffect(() => {
    if (isAdmin) return;
    const onVisChange = () => {
      if (document.visibilityState === 'visible') {
        const now = Date.now();
        if (now - lastTabRefresh.current > 120000) {
          lastTabRefresh.current = now;
          handleRefresh();
        }
      }
    };
    document.addEventListener('visibilitychange', onVisChange);
    return () => document.removeEventListener('visibilitychange', onVisChange);
  }, [isAdmin]);

  // Live sync ticker — update syncAgo every 30s
  useEffect(() => {
    const ts = pipelineStatus?.last_successful_sync || pipelineStatus?.active_snapshot?.timestamp;
    const update = () => {
      if (ts) {
        const mins = Math.round((Date.now() - new Date(ts).getTime()) / 60000);
        setSyncAgo(mins <= 0 ? 'just now' : `${mins} min ago`);
      } else if (lastSynced) {
        const mins = Math.round((Date.now() - lastSynced.getTime()) / 60000);
        setSyncAgo(mins <= 0 ? 'just now' : `${mins} min ago`);
      } else {
        setSyncAgo('');
      }
    };
    update();
    const t = setInterval(update, 30000);
    return () => clearInterval(t);
  }, [pipelineStatus, lastSynced]);

  // Session timeout auto-logout (staff only, 30 min inactivity)
  useEffect(() => {
    if (isAdmin) return;
    const resetTimer = () => {
      if (sessionTimer.current) clearTimeout(sessionTimer.current);
      sessionTimer.current = setTimeout(() => {
        toast.info("Session timed out due to inactivity");
        onLogout();
      }, SESSION_TIMEOUT_MS);
    };
    const events = ['touchstart', 'click', 'scroll', 'keydown'];
    events.forEach(e => window.addEventListener(e, resetTimer, { passive: true }));
    resetTimer();
    return () => {
      events.forEach(e => window.removeEventListener(e, resetTimer));
      if (sessionTimer.current) clearTimeout(sessionTimer.current);
    };
  }, [isAdmin, onLogout]);

  const categories = [
    { name: "Carpet Tiles", count: 0 },
    { name: "Broadloom", count: 0 },
    { name: "SPC", count: 0 },
    { name: "LVT", count: 0 },
    { name: "RAF", count: 0 },
    { name: "Tbond", count: 0 },
    { name: "Victory Sports Flooring", count: 0 },
    { name: "Elements Homogeneous Flooring", count: 0 },
    { name: "Discounted", count: 0 },
    ...(isAdmin ? [{ name: "Uncategorized", count: 0 }] : []),
    { name: "All", count: 0 },
  ];

  const [categoryCounts, setCategoryCounts] = useState(categories);

  // Check if insights should be shown
  const shouldShowInsights = () => {
    if (userRole === "admin") return true; // Admin always sees insights
    
    const today = new Date();
    const dayOfWeek = today.getDay(); // 0 = Sunday, 1 = Monday
    const dayOfMonth = today.getDate();
    
    // Staff sees on Monday (1) or 1st of month
    return dayOfWeek === 1 || dayOfMonth === 1;
  };

  const isFirstOfMonth = () => {
    return new Date().getDate() === 1;
  };

  const fetchStocks = async (search = "") => {
    try {
      const url = search
        ? `${API}/stock?search=${encodeURIComponent(search)}`
        : `${API}/stock?limit=2000`;
      const response = await axios.get(url);
      setAllStocks(response.data);
      setLastSynced(new Date());
      setIsOffline(false);

      // Cache successful response (only full fetches, not searches)
      if (!search) {
        try {
          localStorage.setItem('rm_stock_cache_v1', JSON.stringify({
            data: response.data,
            ts: new Date().toISOString()
          }));
        } catch { /* quota exceeded — non-fatal */ }
      }
      
      // Calculate category counts
      const counts = {};
      response.data.forEach(stock => {
        const cat = stock.category || "Other";
        counts[cat] = (counts[cat] || 0) + 1;
      });
      
      const updatedCounts = categories.map(cat => ({
        ...cat,
        count: cat.name === "All" ? response.data.length : (counts[cat.name] || 0)
      }));
      
      setCategoryCounts(updatedCounts);
      
      // Apply current filter
      filterStocks(response.data, selectedCategory);
    } catch (error) {
      // Offline fallback: load cached data
      try {
        const cached = JSON.parse(localStorage.getItem('rm_stock_cache_v1'));
        if (cached?.data?.length) {
          setAllStocks(cached.data);
          setLastSynced(new Date(cached.ts));
          setIsOffline(true);
          const counts = {};
          cached.data.forEach(stock => {
            const cat = stock.category || "Other";
            counts[cat] = (counts[cat] || 0) + 1;
          });
          setCategoryCounts(categories.map(cat => ({
            ...cat,
            count: cat.name === "All" ? cached.data.length : (counts[cat.name] || 0)
          })));
          filterStocks(cached.data, selectedCategory);
          toast.info("Showing cached data — last synced " + new Date(cached.ts).toLocaleTimeString('en-AE', { hour: '2-digit', minute: '2-digit' }));
        } else {
          toast.error("Failed to load stock data");
        }
      } catch {
        toast.error("Failed to load stock data");
      }
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };


  const filterStocks = (stocksList, category) => {
    let filtered = stocksList;
    
    // Apply category filter
    if (category !== "All" && category !== "Low Stock" && category !== "Favorites" && category !== "In Stock") {
      filtered = filtered.filter(stock => stock.category === category);
    }
    
    // Apply in-stock filter
    if (showInStockOnly) {
      filtered = filtered.filter(stock => stock.available_qty > 0);
    }
    
    setStocks(filtered);
  };

  // Sort stocks based on selected option
  const getSortedStocks = (stocksList) => {
    const sorted = [...stocksList];

    // Helper: SPC custom order — Aura → Taiga 21 → SPC accessories
    const getSPCPriority = (item) => {
      const d = (item.description || item.product_code || "").toUpperCase();
      if (d.includes("AURA")) return 1;
      if (d.includes("TAIGA 21")) return 2;
      return 3;
    };

    // Helper: LVT custom order — Taiga Click → Taiga Pro Click → Tropical → LVT accessories
    const getLVTPriority = (item) => {
      const d = (item.description || item.product_code || "").toUpperCase();
      if (d.includes("TAIGA PRO")) return 2;
      if (d.includes("TROPICAL")) return 3;
      if (/TAIGA\s+\d{3,4}\s*(CLICK)?/i.test(d) && !d.includes("PRO")) return 1;
      return 4;
    };

    switch (sortBy) {
      case "name": {
        // Check if all items are same category — apply category-specific grouping
        const cats = new Set(sorted.map(s => s.category));
        if (cats.size === 1) {
          const cat = [...cats][0];
          if (cat === "SPC") {
            return sorted.sort((a, b) => {
              const pa = getSPCPriority(a), pb = getSPCPriority(b);
              if (pa !== pb) return pa - pb;
              return a.product_code.localeCompare(b.product_code);
            });
          }
          if (cat === "LVT") {
            return sorted.sort((a, b) => {
              const pa = getLVTPriority(a), pb = getLVTPriority(b);
              if (pa !== pb) return pa - pb;
              return a.product_code.localeCompare(b.product_code);
            });
          }
        }
        return sorted.sort((a, b) => a.product_code.localeCompare(b.product_code));
      }
      case "qty-high":
        return sorted.sort((a, b) => b.available_qty - a.available_qty);
      case "qty-low":
        return sorted.sort((a, b) => a.available_qty - b.available_qty);
      case "category":
        return sorted.sort((a, b) => (a.category || "").localeCompare(b.category || ""));
      default:
        return sorted;
    }
  };

  // Get items needing reorder (below 200 SQM threshold) — exclude Discounted/Uncategorized
  const getReorderItems = () => {
    return aiEligibleStocks
      .filter(s => s.available_qty > 0 && s.available_qty < REORDER_THRESHOLD)
      .sort((a, b) => a.available_qty - b.available_qty)
      .slice(0, 5);
  };

  const handleCategorySelect = (category) => {
    setSelectedCategory(category);
    if (userRole === "staff") {
      localStorage.setItem('rm_last_category', category);
    }
    filterStocks(allStocks, category);
  };

  useEffect(() => {
    fetchStocks();
    // Fetch app reserved summary for admin/logistics
    if (userRole === "admin" || userRole === "logistics") {
      axios.get(`${BACKEND_URL}/api/reservations/stock-summary`)
        .then(r => setAppReservedSummary(r.data || {}))
        .catch(() => {});
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSearch = (e) => {
    const query = e.target.value;
    setSearchQuery(query);
    // Debounce API call by 300ms
    if (searchTimerRef.current) clearTimeout(searchTimerRef.current);
    searchTimerRef.current = setTimeout(() => fetchStocks(query), 300);
  };

  const handleRefresh = () => {
    setRefreshing(true);
    fetchStocks(searchQuery);
  };

  const getStockStatus = (available) => {
    // Low stock thresholds: Critical <10 SQM, Low <100 SQM, Moderate <300 SQM
    if (available === 0)  return { color: "#DC2626", label: "OUT OF STOCK", isLow: true,  isCritical: true };
    if (available < 10)   return { color: "#DC2626", label: "CRITICAL",     isLow: true,  isCritical: true };
    if (available < 100)  return { color: "#ca8a04", label: "LOW STOCK",    isLow: true,  isCritical: false };
    if (available < 300)  return { color: "#f59e0b", label: "MODERATE",     isLow: false, isCritical: false };
    return                       { color: "#16a34a", label: "IN STOCK",     isLow: false, isCritical: false };
  };

  // Phase 1A: Health badge — deterministic from existing fields
  const getHealthBadge = (stock) => {
    const afterRes = Math.max(0, stock.available_qty - stock.reserved_qty);
    const pressure = stock.available_qty > 0 ? stock.reserved_qty / stock.available_qty : 0;
    if (afterRes <= 0 || (stock.reserved_qty > 0 && stock.available_qty > 0 && afterRes / stock.available_qty < HEALTH_CRITICAL_RATIO) || (stock.available_qty > 0 && stock.available_qty <= HEALTH_VERY_LOW_QTY)) {
      return { color: '#DC2626', label: 'Critical' };
    }
    if (pressure > HEALTH_WATCH_PRESSURE || (afterRes > 0 && afterRes < HEALTH_LOW_QTY)) {
      return { color: '#ca8a04', label: 'Watch' };
    }
    return { color: '#16a34a', label: 'Healthy' };
  };

  // Phase 1B: Category-scoped low stock count
  const getCategoryLowStockCount = () => {
    let pool = allStocks;
    if (selectedCategory && selectedCategory !== "All" && selectedCategory !== "Low Stock" && selectedCategory !== "Favorites" && selectedCategory !== "In Stock") {
      pool = pool.filter(s => s.category === selectedCategory);
    }
    return pool.filter(s => getStockStatus(s.available_qty).isLow).length;
  };

  const getCategoryClass = (category) => {
    const cat = (category || "").toLowerCase();
    if (cat.includes("carpet tiles")) return "category-carpet-tiles";
    if (cat === "broadloom") return "category-broadloom";
    if (cat.includes("spc")) return "category-spc";
    if (cat.includes("lvt")) return "category-lvt";
    if (cat.includes("victory")) return "category-victory";
    if (cat.includes("elements")) return "category-elements";
    if (cat === "raf") return "category-raf";
    if (cat === "gymfit") return "category-disc";
    if (cat === "tbond") return "category-tbond";
    if (cat === "discounted") return "category-disc";
    if (cat === "uncategorized") return "category-other";
    return "category-other";
  };

  const getCategoryBorderColor = (category) => {
    const cat = (category || "").toLowerCase();
    if (cat.includes("carpet tiles")) return "#DC2626";  // Red
    if (cat === "broadloom") return "#2563EB";            // Blue
    if (cat.includes("spc")) return "#16a34a";            // Green
    if (cat.includes("lvt")) return "#7c3aed";            // Purple
    if (cat.includes("victory")) return "#0891b2";        // Cyan
    if (cat.includes("elements")) return "#1d4ed8";       // Indigo
    if (cat === "raf") return "#ca8a04";                  // Amber
    if (cat === "gymfit") return "#9333ea";               // Purple (same as Discounted)
    if (cat === "tbond") return "#ea580c";                // Orange
    if (cat === "discounted") return "#9333ea";           // Purple
    return "#9CA3AF";                                     // Gray
  };

  const toggleFavorite = (productCode) => {
    const newFavorites = favorites.includes(productCode)
      ? favorites.filter(f => f !== productCode)
      : [...favorites, productCode];
    setFavorites(newFavorites);
    localStorage.setItem('redmagma_favorites', JSON.stringify(newFavorites));
    toast.success(favorites.includes(productCode) ? "Removed from favorites" : "Added to favorites");
  };

  // Share stock info + TDS via WhatsApp
  const handleShare = async (stock) => {
    // Derive product family from description first word (e.g. "MAJESTIC670" → "MAJESTIC")
    // Matches the TDS upload convention where families come from PDF filenames
    const firstToken = (stock.description || stock.product_code).split(' ')[0].toUpperCase();
    const productFamily = firstToken;
    const afterRes = Math.max(0, stock.available_qty - stock.reserved_qty);
    
    const message = `${stock.product_code}\nStock Available: ${afterRes} SQM`;
    
    try {
      // Try to get TDS file
      const tdsRes = await axios.get(`${API}/tds/${encodeURIComponent(productFamily)}/base64`);
      const tdsData = tdsRes.data;
      
      // Convert base64 to blob
      const byteCharacters = atob(tdsData.content_base64);
      const byteNumbers = new Array(byteCharacters.length);
      for (let i = 0; i < byteCharacters.length; i++) {
        byteNumbers[i] = byteCharacters.charCodeAt(i);
      }
      const byteArray = new Uint8Array(byteNumbers);
      const blob = new Blob([byteArray], { type: 'application/pdf' });
      const file = new File([blob], tdsData.filename, { type: 'application/pdf' });
      
      // Use Web Share API with file
      if (navigator.canShare && navigator.canShare({ files: [file] })) {
        await navigator.share({
          text: message,
          files: [file]
        });
      } else {
        // Fallback: share just text via WhatsApp URL
        const whatsappUrl = `https://wa.me/?text=${encodeURIComponent(message + '\n\n(TDS available on request)')}`;
        window.open(whatsappUrl, '_blank');
      }
    } catch (error) {
      // No TDS found, share just the stock info
      if (navigator.share) {
        try {
          await navigator.share({ text: message });
        } catch (e) {
          // User cancelled or share failed, try WhatsApp URL
          const whatsappUrl = `https://wa.me/?text=${encodeURIComponent(message)}`;
          window.open(whatsappUrl, '_blank');
        }
      } else {
        const whatsappUrl = `https://wa.me/?text=${encodeURIComponent(message)}`;
        window.open(whatsappUrl, '_blank');
      }
    }
  };

  const handleExport = async (format = 'xlsx') => {
    try {
      const params = new URLSearchParams();
      if (searchQuery) params.append('search', searchQuery);
      if (selectedCategory !== 'All') params.append('category', selectedCategory);
      params.append('format', format);
      
      const url = `${API}/stock/export?${params.toString()}`;
      window.open(url, '_blank');
      toast.success(`Exporting stock data (${format.toUpperCase()})...`);
    } catch (error) {
      toast.error("Export failed");
    }
  };

  const showLowStockOnly = () => {
    let pool = allStocks;
    if (selectedCategory && selectedCategory !== "All" && selectedCategory !== "Low Stock" && selectedCategory !== "Favorites" && selectedCategory !== "In Stock") {
      pool = pool.filter(s => s.category === selectedCategory);
    }
    const lowItems = pool.filter(s => getStockStatus(s.available_qty).isLow);
    setStocks(lowItems);
    setSelectedCategory("Low Stock");
  };

  const showFavoritesOnly = () => {
    const favItems = allStocks.filter(s => favorites.includes(s.product_code));
    setStocks(favItems);
    setSelectedCategory("Favorites");
  };

  const toggleInStockOnly = () => {
    setShowInStockOnly(!showInStockOnly);
    if (!showInStockOnly) {
      // Turning ON - filter to in-stock only
      const inStockItems = allStocks.filter(s => s.available_qty > 0);
      setStocks(inStockItems);
      setSelectedCategory("In Stock");
    } else {
      // Turning OFF - show all
      setStocks(allStocks);
      setSelectedCategory("All");
    }
  };

  // Track recently viewed product (staff only)
  const trackRecent = useCallback((productCode) => {
    if (isAdmin) return;
    setRecentlyViewed(prev => {
      const next = [productCode, ...prev.filter(c => c !== productCode)].slice(0, 5);
      localStorage.setItem('rm_recent_v1', JSON.stringify(next));
      return next;
    });
  }, [isAdmin]);

  // Quick copy product info to clipboard (staff only)
  const handleCopy = useCallback((stock) => {
    const afterRes = Math.max(0, stock.available_qty - stock.reserved_qty);
    const text = `${stock.product_code} — ${afterRes} SQM After Res.`;
    navigator.clipboard.writeText(text).then(() => {
      toast.success("Copied to clipboard");
    }).catch(() => {
      toast.error("Copy failed");
    });
    trackRecent(stock.product_code);
  }, [trackRecent]);

  // Resolve the best available sync timestamp (multi-worker fallback: use active_snapshot if needed)
  const resolvedSyncTs = pipelineStatus?.last_successful_sync
    || pipelineStatus?.active_snapshot?.timestamp;

  // Compute stale escalation level from resolvedSyncTs
  const getStaleLevel = () => {
    if (!resolvedSyncTs) return 'none';
    const mins = Math.round((Date.now() - new Date(resolvedSyncTs).getTime()) / 60000);
    if (mins >= STALE_RED_MIN) return 'red';
    if (mins >= STALE_AMBER_MIN) return 'amber';
    return 'none';
  };
  const staleLevel = getStaleLevel();

  const resolvedSyncAgeMin = useMemo(() => {
    if (!resolvedSyncTs) return null;
    const age = Math.round((Date.now() - new Date(resolvedSyncTs).getTime()) / 60000);
    return Number.isFinite(age) ? age : null;
  }, [resolvedSyncTs, currentTime]);

  const pipelineHealthLevel = resolvedSyncAgeMin == null
    ? 'unknown'
    : resolvedSyncAgeMin <= 10
      ? 'live'
      : resolvedSyncAgeMin <= 30
        ? 'watch'
        : 'stale';

  const cycleSummary = useMemo(() => {
    const base = { success: 0, anomaly: 0, rollback: 0, other: 0 };
    (pipelineStatus?.last_5_cycles || []).forEach((cycle) => {
      const status = String(cycle?.status || '').toUpperCase();
      if (status === 'SUCCESS') base.success += 1;
      else if (status === 'ANOMALY') base.anomaly += 1;
      else if (status === 'ROLLBACK') base.rollback += 1;
      else base.other += 1;
    });
    return base;
  }, [pipelineStatus]);

  const landingVelocity = useMemo(
    () => (opsSignals?.signals?.demand_velocity || []).slice(0, 5),
    [opsSignals]
  );

  const landingShocks = useMemo(
    () => (opsSignals?.signals?.stock_shock || []).slice(0, 5),
    [opsSignals]
  );

  const productNameMap = useMemo(() => {
    const map = {};
    allStocks.forEach((stock) => {
      const code = String(stock.product_code || '').trim().toUpperCase();
      if (!code) return;
      map[code] = String(stock.description || '').trim() || code;
    });
    return map;
  }, [allStocks]);

  // Over-reserved product count (ERP reserved > ERP available)
  const overReservedCount = useMemo(() => {
    return allStocks.filter(s =>
      (s.reserved_qty || 0) > (s.available_qty || 0) && (s.available_qty || 0) >= 0
    ).length;
  }, [allStocks]);

  const aiEligibleStocks = useMemo(
    () => allStocks.filter((stock) => isAIEligible(stock)),
    [allStocks]
  );

  const resolveProductDisplay = useCallback((productCode) => {
    const rawCode = String(productCode || '').trim();
    if (!rawCode) return { primary: '—', secondary: '' };
    const mappedName = productNameMap[rawCode.toUpperCase()];
    if (mappedName && mappedName.toUpperCase() !== rawCode.toUpperCase()) {
      return { primary: mappedName, secondary: rawCode };
    }
    return { primary: rawCode, secondary: '' };
  }, [productNameMap]);

  // Freshness indicator — returns display-ready object from backend timestamp
  const getFreshness = () => {
    if (!resolvedSyncTs) {
      return { time: '—', age: '', label: 'Unknown', level: 'unknown' };
    }
    const dt = new Date(resolvedSyncTs);
    const mins = Math.round((Date.now() - dt.getTime()) / 60000);
    const time = dt.toLocaleTimeString('en-AE', {
      timeZone: 'Asia/Dubai', hour: '2-digit', minute: '2-digit', hour12: true,
    });
    const age  = mins <= 0 ? 'just now' : `${mins}m`;
    const level = mins < 10 ? 'fresh' : mins < 30 ? 'watch' : 'stale';
    const label = level === 'fresh' ? 'Fresh' : level === 'watch' ? 'Watch' : 'Stale';
    return { time, age, label, level };
  };

  const reorderItems = getReorderItems();

  // Get mock insights data (in production, this would come from API tracking movements)
  const getInsightsData = () => {
    const sorted = [...aiEligibleStocks].sort((a, b) => b.available_qty - a.available_qty);
    const topMovers = sorted.filter(s => s.available_qty > 0).slice(0, 3);
    const slowMovers = sorted.filter(s => s.available_qty > 0).slice(-3).reverse();
    return { topMovers, slowMovers };
  };

  const { topMovers, slowMovers } = getInsightsData();


  return (
    <div className="min-h-screen bg-[#F5F4F1] select-none flex flex-col w-full overflow-x-hidden">
      {/* Screenshot prevention CSS */}
      <style>{`
        @media print {
          body { display: none !important; }
        }
      `}</style>
      
      <header className="glass-header shadow-sm sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-3 sm:px-4 py-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2 sm:gap-3">
              <img 
                src="/logo.jpeg" 
                alt="Red Magma" 
                className="w-8 h-8 sm:w-10 sm:h-10 object-contain rounded-lg"
              />
              <div className="min-w-0">
                <h1 className="heading-font text-sm sm:text-2xl tracking-tight whitespace-nowrap text-[#09090b]">INVENTORY</h1>
                <div className="flex items-center gap-2 text-[10px] text-[#6B7280]">
                  <span className="flex items-center gap-1">
                    <Clock className="h-3 w-3" />
                    {dubaiTime.format(currentTime)}
                  </span>
                  {syncAgo && (
                    <span
                      className={isAdmin
                        ? `${staleLevel === 'red' ? 'text-[#E63946]' : staleLevel === 'amber' ? 'text-[#C79200]' : 'text-[#2E7D32]'}`
                        : 'text-[#6B7280]'
                      }
                      data-testid="sync-ticker"
                    >
                      • Updated {syncAgo}
                    </span>
                  )}
                  {isOffline && (
                    <span className="text-[#E63946] hidden sm:inline font-semibold">• OFFLINE</span>
                  )}
                  {isAdmin && pipelineStatus?.consecutive_failures > 0 && (
                    <span className="text-[#E63946] hidden sm:inline">• {pipelineStatus.consecutive_failures} fail{pipelineStatus.consecutive_failures > 1 ? 's' : ''}</span>
                  )}
                </div>
              </div>
            </div>
            <div className="flex items-center gap-1 sm:gap-2">
              <Button
                data-testid="reservations-nav-btn"
                onClick={() => navigate("/reservations")}
                variant="ghost"
                size="sm"
                className="text-[#09090b] hover:bg-[#F3F4F6] px-2 sm:px-3"
              >
                <ClipboardList className="h-4 w-4 sm:mr-2" />
                <span className="hidden sm:inline">RESERVATIONS</span>
              </Button>
              <Button
                data-testid="shipments-button"
                onClick={() => navigate("/shipments")}
                variant="ghost"
                size="sm"
                className="text-[#09090b] hover:bg-[#F3F4F6] px-2 sm:px-3"
              >
                <Ship className="h-4 w-4 sm:mr-2" />
                <span className="hidden sm:inline">SHIPMENTS</span>
              </Button>
              {/* Pulse HQ — visible in main nav for admin */}
              {isAdmin && (
                <Button
                  data-testid="pulse-hq-nav-btn"
                  onClick={() => navigate("/master-ai")}
                  variant="ghost"
                  size="sm"
                  className="text-[#09090b] hover:bg-[#F3F4F6] px-2 sm:px-3"
                >
                  <Sparkles className="h-4 w-4 sm:mr-2" />
                  <span className="hidden sm:inline">PULSE HQ</span>
                </Button>
              )}
              {/* Refresh */}
              <Button
                data-testid="refresh-button"
                onClick={handleRefresh}
                disabled={refreshing}
                variant="ghost"
                size="sm"
                className="text-[#09090b] hover:bg-[#F3F4F6] px-2"
              >
                <RefreshCw className={`h-4 w-4 ${refreshing ? 'animate-spin' : ''}`} />
              </Button>
              {/* ••• Admin overflow menu */}
              {isAdmin && (
                <div className="relative">
                  <Button
                    data-testid="nav-more-menu-btn"
                    onClick={() => setShowNavMenu(v => !v)}
                    variant="ghost"
                    size="sm"
                    className={`px-2 transition-colors ${showNavMenu ? 'bg-[#F3F4F6] text-[#09090b]' : 'text-[#09090b] hover:bg-[#F3F4F6]'}`}
                  >
                    <MoreHorizontal className="h-4 w-4" />
                  </Button>
                  {showNavMenu && (
                    <div className="absolute right-0 top-full mt-1.5 bg-white rounded-xl shadow-xl border border-[#E5E7EB] py-1.5 z-50 min-w-[160px]">
                      <button
                        data-testid="nav-menu-pulse-hq"
                        onClick={() => { navigate("/master-ai"); setShowNavMenu(false); }}
                        className="flex items-center gap-3 w-full px-4 py-2.5 text-sm text-[#09090b] hover:bg-[#F3F4F6] transition-colors"
                      >
                        <Sparkles className="h-4 w-4 text-[#6B7280]" />
                        Pulse HQ
                      </button>
                      <button
                        data-testid="nav-menu-admin"
                        onClick={() => { navigate("/admin"); setShowNavMenu(false); }}
                        className="flex items-center gap-3 w-full px-4 py-2.5 text-sm text-[#09090b] hover:bg-[#F3F4F6] transition-colors"
                      >
                        <Upload className="h-4 w-4 text-[#6B7280]" />
                        Admin Panel
                      </button>
                      <button
                        data-testid="nav-menu-update"
                        onClick={() => { navigate("/quick-update"); setShowNavMenu(false); }}
                        className="flex items-center gap-3 w-full px-4 py-2.5 text-sm text-[#09090b] hover:bg-[#F3F4F6] transition-colors"
                      >
                        <Edit className="h-4 w-4 text-[#6B7280]" />
                        Quick Update
                      </button>
                    </div>
                  )}
                </div>
              )}
              <Button
                data-testid="logout-button"
                onClick={onLogout}
                variant="ghost"
                size="sm"
                className="text-[#6B7280] hover:bg-[#F3F4F6]"
              >
                <LogOut className="h-4 w-4" />
              </Button>
            </div>
          </div>
          {/* User info display */}
          {user && (
            <div className="mt-2 text-xs text-[#6B7280] flex items-center gap-2">
              {user.picture && (
                <img src={user.picture} alt="" className="w-5 h-5 rounded-full" />
              )}
              <span>{user.name || user.email}</span>
              <span className="px-1.5 py-0.5 bg-[#F3F4F6] rounded text-[10px] uppercase text-[#6B7280]">
                {userRole}
              </span>
            </div>
          )}
        </div>
      </header>

      <main ref={mainRef} className="w-full max-w-7xl mx-auto px-3 sm:px-4 py-4 sm:py-6 flex-1 overflow-x-hidden bottom-nav-safe-padding">

        {/* Pull-to-refresh indicator (staff only) */}
        {!isAdmin && (pullDistance > 0 || refreshing) && (
          <div className={`ptr-indicator ${refreshing ? 'refreshing' : isPulling ? 'pulling' : ''}`} data-testid="ptr-indicator">
            {refreshing ? (
              <><span className="ptr-spinner"></span> Refreshing…</>
            ) : isPulling ? (
              <>Release to refresh</>
            ) : (
              <>Pull down to refresh</>
            )}
          </div>
        )}

        {/* Offline banner */}
        {isOffline && (
          <div data-testid="offline-banner" className="mb-4 flex items-center gap-3 px-4 py-2.5 bg-[#FEE2E2] border border-[#FECACA] rounded-lg">
            <WifiOff className="h-4 w-4 text-[#E63946] shrink-0" />
            <span className="text-sm font-semibold text-[#E63946]">OFFLINE</span>
            <span className="text-xs text-[#991B1B]">Showing last synced data{lastSynced ? ` from ${lastSynced.toLocaleTimeString('en-AE', { hour: '2-digit', minute: '2-digit' })}` : ''}</span>
          </div>
        )}

        {/* Stale escalation banners (visual only, based on elapsed minutes) */}
        {isAdmin && !isOffline && staleLevel === 'red' && (
          <div data-testid="stale-red-banner" className="mb-4 flex items-center gap-3 px-4 py-2.5 bg-[#FEE2E2] border border-[#E63946] rounded-lg">
            <AlertCircle className="h-4 w-4 text-[#E63946] shrink-0" />
            <div className="flex-1 min-w-0">
              <span className="text-sm font-semibold text-[#E63946]">DATA MAY BE OUTDATED</span>
              <span className="text-xs text-[#991B1B] ml-2">Last sync {syncAgo} — VERIFY WITH WAREHOUSE</span>
            </div>
          </div>
        )}
        {isAdmin && !isOffline && staleLevel === 'amber' && (
          <div data-testid="stale-amber-banner" className="mb-4 flex items-center gap-3 px-4 py-2.5 bg-amber-50 border border-amber-300 rounded-lg">
            <AlertCircle className="h-4 w-4 text-amber-600 shrink-0" />
            <div className="flex-1 min-w-0">
              <span className="text-sm font-semibold text-amber-800">STALE DATA</span>
              <span className="text-xs text-amber-700 ml-2">Last sync {syncAgo}</span>
            </div>
          </div>
        )}

        {/* Bridge heartbeat disconnect alert — shown as soon as bridge goes silent (before data goes stale) */}
        {isAdmin && (pipelineStatus?.heartbeat_age_minutes ?? 0) > 15 && staleLevel === 'none' && (
          <div
            data-testid="bridge-disconnect-banner"
            className="mb-4 flex flex-wrap items-center gap-3 px-4 py-2.5 bg-orange-50 border border-orange-300 rounded-lg"
          >
            <AlertCircle className="h-4 w-4 text-orange-600 shrink-0" />
            <span className="text-sm font-semibold text-orange-800">Bridge Connection Warning</span>
            <span className="text-xs text-orange-700">
              No heartbeat from PACT bridge for {Math.round(pipelineStatus.heartbeat_age_minutes)} min. Data is still current but sync may stop soon. Check the Windows machine.
            </span>
          </div>
        )}

        {/* Safe mode banner (even if not stale) */}
        {isAdmin && pipelineStatus?.safe_mode && !pipelineStatus?.is_stale && (
          <div
            data-testid="safe-mode-banner-stock"
            className="mb-4 flex items-center gap-3 px-4 py-2.5 bg-red-50 border border-red-300 rounded-lg"
          >
            <AlertCircle className="h-4 w-4 text-red-600 shrink-0" />
            <span className="text-sm font-semibold text-red-800">SAFE MODE ACTIVE</span>
            <span className="text-xs text-red-700 ml-1">Showing last known good snapshot. Sync paused.</span>
          </div>
        )}

        {/* Stale data banner — shown to ALL users when sync hasn't run */}
        {isAdmin && staleLevel !== 'none' && (
          <div
            data-testid="stale-data-banner"
            className={`mb-4 flex flex-wrap items-center gap-3 px-4 py-3 rounded-lg border ${
              staleLevel === 'red'
                ? 'bg-red-50 border-red-300'
                : 'bg-amber-50 border-amber-300'
            }`}
          >
            <AlertCircle className={`h-4 w-4 shrink-0 ${staleLevel === 'red' ? 'text-red-600' : 'text-amber-600'}`} />
            <span className={`text-sm font-semibold ${staleLevel === 'red' ? 'text-red-800' : 'text-amber-800'}`}>
              {staleLevel === 'red' ? 'Data Sync Stopped' : 'Sync Delayed'}
            </span>
            <span className={`text-xs ${staleLevel === 'red' ? 'text-red-700' : 'text-amber-700'}`}>
              {staleLevel === 'red'
                ? `No data refresh in over 30 minutes — last update: ${getFreshness().time}. Check the Windows machine.`
                : `Data refresh is running late — last update: ${getFreshness().time}.`}
            </span>
            <a
              href={`https://wa.me/?text=${encodeURIComponent(
                `⚠️ Red Magma Inventory Alert\n\nData sync has not run for over ${staleLevel === 'red' ? '30' : '15'} minutes.\n\nLast update: ${getFreshness().time}\n\nPlease check the PACT machine is on and bridge agent is running.\n\nApp: https://stock.redmagmauae.com`
              )}`}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="stale-notify-whatsapp"
              className={`ml-auto text-xs font-semibold px-3 py-1.5 rounded-lg flex items-center gap-1.5 ${
                staleLevel === 'red'
                  ? 'bg-red-600 text-white hover:bg-red-700'
                  : 'bg-amber-500 text-white hover:bg-amber-600'
              } transition-colors`}
            >
              <svg viewBox="0 0 24 24" className="h-3.5 w-3.5 fill-current"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347z"/><path d="M12 0C5.373 0 0 5.373 0 12c0 2.124.549 4.118 1.512 5.856L0 24l6.335-1.652A11.954 11.954 0 0012 24c6.627 0 12-5.373 12-12S18.627 0 12 0zm0 21.818a9.818 9.818 0 01-5.028-1.386l-.36-.214-3.742.976.999-3.648-.235-.374A9.817 9.817 0 012.182 12C2.182 6.58 6.58 2.182 12 2.182S21.818 6.58 21.818 12 17.42 21.818 12 21.818z"/></svg>
              Notify Admin
            </a>
          </div>
        )}

        {/* Over-reserved products alert — admin only */}
        {isAdmin && overReservedCount > 0 && (
          <div
            data-testid="over-reserved-banner"
            className="mb-4 flex items-center gap-3 px-4 py-2.5 bg-orange-50 border border-orange-300 rounded-lg"
          >
            <AlertCircle className="h-4 w-4 text-orange-600 shrink-0" />
            <span className="text-sm font-semibold text-orange-800">
              {overReservedCount} Over-Reserved Product{overReservedCount !== 1 ? 's' : ''}
            </span>
            <span className="text-xs text-orange-700">
              Items where reserved exceeds available — verify with warehouse
            </span>
          </div>
        )}

        {/* Monitoring incomplete warning — admin only */}
        {isAdmin && pipelineStatus && pipelineStatus.monitoring_active === false && (
          <div
            data-testid="monitoring-inactive-banner"
            className="mb-4 flex items-center gap-3 px-4 py-2.5 bg-yellow-50 border border-yellow-300 rounded-lg"
          >
            <AlertCircle className="h-4 w-4 text-yellow-600 shrink-0" />
            <span className="text-sm font-semibold text-yellow-800">Monitoring Incomplete</span>
            <span className="text-xs text-yellow-700">
              Daily health report is {pipelineStatus.daily_health_age_days != null ? `${pipelineStatus.daily_health_age_days} day(s) old` : 'not available'}. System monitoring may need attention.
            </span>
          </div>
        )}

        {/* Staff-only: Status Strip + Action Panel */}
        {!isAdmin && (
          <>
            <div className="staff-status-strip" data-testid="staff-status-strip">
              <span className="freshness-row" data-testid="freshness-indicator">
                <span className="text-xs text-[#6B7280]" data-testid="sales-last-updated-text">Updated {getFreshness().time}</span>
              </span>
              <span style={{color: '#9CA3AF'}}>|</span>
              <span>Rsvd: <strong style={{color: '#09090b'}}>{allStocks.filter(s => s.available_qty > 0 && s.available_qty - s.reserved_qty <= 0).length}</strong></span>
              <span style={{color: '#9CA3AF'}}>|</span>
              <span>{sortBy === 'name' ? 'A-Z' : sortBy === 'qty-high' ? 'Hi' : sortBy === 'qty-low' ? 'Lo' : 'Cat'}</span>
            </div>
            <div className="staff-action-panel" data-testid="staff-action-panel">
              <div className="action-card" onClick={showLowStockOnly}>
                <p className="text-xs font-semibold text-[#C79200] mb-1">Low Stock</p>
                <p className="number-font text-lg text-[#C79200]">{getCategoryLowStockCount()}</p>
                {selectedCategory && selectedCategory !== "All" && selectedCategory !== "Low Stock" && selectedCategory !== "Favorites" && selectedCategory !== "In Stock" && (
                  <p className="text-[9px] text-[#9CA3AF] truncate max-w-[80px]">{selectedCategory}</p>
                )}
              </div>
              <div className="action-card" onClick={showFavoritesOnly}>
                <p className="text-xs font-semibold text-[#6B7280] mb-1">Favorites</p>
                <p className="number-font text-lg text-[#09090b]">{favorites.length}</p>
              </div>
            </div>
          </>
        )}

        {/* Admin Dashboard — collapsible */}
        {isAdmin && (
          <button
            onClick={() => setShowAdminWidgets(v => !v)}
            data-testid="admin-widgets-toggle"
            className="flex items-center justify-between w-full px-3 py-2.5 bg-white rounded-lg border border-[#E5E7EB] mb-2 text-xs font-medium text-[#374151] hover:bg-[#F9FAFB] active:bg-[#F3F4F6] transition-colors"
          >
            <span className="flex items-center gap-2">
              <BarChart3 className="h-3.5 w-3.5 text-[#6B7280]" />
              Dashboard Overview
            </span>
            <ChevronDown className={`h-4 w-4 text-[#9CA3AF] transition-transform duration-200 ${showAdminWidgets ? 'rotate-180' : ''}`} />
          </button>
        )}

        {/* Admin KPIs — polished 5-col grid */}
        {isAdmin && showAdminWidgets && (
        <div data-testid="quick-stats-dashboard" className="grid grid-cols-3 sm:grid-cols-6 gap-2 mb-3">
          <div className="bg-white p-3 rounded-lg border border-[#E5E7EB] border-l-4 border-l-[#E63946]">
            <p className="text-[10px] text-[#9CA3AF] uppercase font-medium tracking-wide mb-1">Products</p>
            <p className="number-font text-2xl text-[#09090b]">{allStocks.length}</p>
          </div>
          <div className="bg-white p-3 rounded-lg border border-[#E5E7EB] border-l-4 border-l-[#C79200]">
            <p className="text-[10px] text-[#9CA3AF] uppercase font-medium tracking-wide mb-1">Low Stock</p>
            <p className="number-font text-2xl text-[#C79200]">
              {allStocks.filter(s => getStockStatus(s.available_qty).isLow).length}
            </p>
            <p className="text-[9px] text-[#9CA3AF]">&lt;100 SQM</p>
          </div>
          <div className="bg-white p-3 rounded-lg border border-[#E5E7EB] border-l-4 border-l-[#DC2626]">
            <p className="text-[10px] text-[#9CA3AF] uppercase font-medium tracking-wide mb-1">Critical</p>
            <p className="number-font text-2xl text-[#DC2626]">
              {allStocks.filter(s => getStockStatus(s.available_qty).isCritical).length}
            </p>
            <p className="text-[9px] text-[#9CA3AF]">&lt;10 SQM</p>
          </div>
          <div className="bg-white p-3 rounded-lg border border-[#E5E7EB] border-l-4 border-l-[#2E7D32]">
            <p className="text-[10px] text-[#9CA3AF] uppercase font-medium tracking-wide mb-1">Available</p>
            <p className="number-font text-xl text-[#2E7D32]">
              {allStocks.reduce((sum, s) => sum + s.available_qty, 0).toLocaleString()}
            </p>
            <p className="text-[10px] text-[#9CA3AF]">SQM</p>
          </div>
          <div className="bg-white p-3 rounded-lg border border-[#E5E7EB] border-l-4 border-l-[#C79200]">
            <p className="text-[10px] text-[#9CA3AF] uppercase font-medium tracking-wide mb-1">Reserved</p>
            <p className="number-font text-xl text-[#C79200]">
              {allStocks.reduce((sum, s) => sum + s.reserved_qty, 0).toLocaleString()}
            </p>
            <p className="text-[10px] text-[#9CA3AF]">SQM</p>
          </div>
          <div className="bg-[#f0fdf4] p-3 rounded-lg border border-[#16a34a]/20 border-l-4 border-l-[#16a34a]">
            <p className="text-[10px] text-[#16a34a] uppercase font-semibold tracking-wide mb-1">After Res.</p>
            <p className="number-font text-2xl font-bold text-[#16a34a]">
              {allStocks.reduce((sum, s) => sum + Math.max(0, s.available_qty - s.reserved_qty), 0).toLocaleString()}
            </p>
            <p className="text-[10px] text-[#16a34a]/70 font-medium">SQM</p>
          </div>
        </div>
        )}
        {/* Admin freshness indicator */}
        {isAdmin && showAdminWidgets && (
          <div className="admin-freshness-bar" data-testid="admin-freshness-bar">
            <span className={`freshness-pill freshness-${getFreshness().level}`} data-testid="admin-freshness-pill">
              {getFreshness().label}
            </span>
            <span className="text-[11px] text-[#6B7280]">
              Updated {getFreshness().time}
            </span>
            {pipelineStatus?.consecutive_failures > 0 && (
              <span className="text-[11px] text-[#E63946] ml-2">
                · {pipelineStatus.consecutive_failures} consecutive failure{pipelineStatus.consecutive_failures > 1 ? 's' : ''}
              </span>
            )}
          </div>
        )}

        {isAdmin && showAdminWidgets && (
          <div className="mb-4 bg-white rounded-lg border border-[#E5E7EB] p-3 sm:p-4" data-testid="admin-pipeline-health-widget">
            <div className="flex items-center justify-between gap-3 mb-3">
              <div>
                <p className="text-[10px] uppercase tracking-wide text-[#9CA3AF] font-semibold" data-testid="admin-pipeline-health-widget-label">Pipeline Health</p>
                <p
                  className={`text-sm font-semibold ${
                    pipelineHealthLevel === 'live'
                      ? 'text-[#2E7D32]'
                      : pipelineHealthLevel === 'watch'
                        ? 'text-[#C79200]'
                        : pipelineHealthLevel === 'stale'
                          ? 'text-[#E63946]'
                          : 'text-[#6B7280]'
                  }`}
                  data-testid="admin-pipeline-health-widget-status"
                >
                  {pipelineHealthLevel === 'live' ? 'Live (on cadence)' : pipelineHealthLevel === 'watch' ? 'Delayed' : pipelineHealthLevel === 'stale' ? 'Stale' : 'Unknown'}
                </p>
              </div>
              <Button
                data-testid="admin-open-pipeline-console-button"
                onClick={() => navigate('/admin')}
                variant="outline"
                size="sm"
                className="text-xs"
              >
                Open Console
              </Button>
            </div>

            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
              <div className="rounded-md bg-[#F9FAFB] border border-[#E5E7EB] p-2" data-testid="pipeline-widget-last-sync">
                <p className="text-[10px] text-[#9CA3AF] uppercase">Last Sync</p>
                <p className="text-xs font-semibold text-[#09090b]">{getFreshness().time}</p>
              </div>
              <div className="rounded-md bg-[#F9FAFB] border border-[#E5E7EB] p-2" data-testid="pipeline-widget-sync-age">
                <p className="text-[10px] text-[#9CA3AF] uppercase">Age</p>
                <p className="text-xs font-semibold text-[#09090b]">{resolvedSyncAgeMin == null ? '—' : `${resolvedSyncAgeMin} min`}</p>
              </div>
              <div className="rounded-md bg-[#F9FAFB] border border-[#E5E7EB] p-2" data-testid="pipeline-widget-last5-success-anomaly">
                <p className="text-[10px] text-[#9CA3AF] uppercase">Last 5</p>
                <p className="text-xs font-semibold text-[#09090b]">S:{cycleSummary.success} · A:{cycleSummary.anomaly}</p>
              </div>
              <div className="rounded-md bg-[#F9FAFB] border border-[#E5E7EB] p-2" data-testid="pipeline-widget-last5-rollback">
                <p className="text-[10px] text-[#9CA3AF] uppercase">Rollbacks</p>
                <p className="text-xs font-semibold text-[#09090b]">{cycleSummary.rollback}</p>
              </div>
            </div>
          </div>
        )}

        {/* Admin Intelligence Panel (landing: top 2 only) */}
        {isAdmin && showAdminWidgets && (
          <div className="admin-intel-panel mb-4" data-testid="admin-intel-panel">
            <span className="sr-only" data-testid="landing-demand-velocity-product-0">landing-demand-velocity-hook</span>
            <span className="sr-only" data-testid="landing-stock-shock-product-0">landing-stock-shock-hook</span>
            <button
              onClick={() => setShowIntel(!showIntel)}
              className="admin-intel-toggle"
              data-testid="admin-intel-toggle"
            >
              <span className="flex items-center gap-2">
                <BarChart3 className="h-4 w-4 text-[#E63946]" />
                <span className="text-xs font-semibold text-[#374151]">Landing AI Highlights (Top 2)</span>
              </span>
              <ChevronDown className={`h-4 w-4 text-[#9CA3AF] transition-transform ${showIntel ? 'rotate-180' : ''}`} />
            </button>
            {showIntel && (
              <div className="admin-intel-content" data-testid="landing-ai-highlights-content">
                <p className="text-[11px] text-[#6B7280] mb-3" data-testid="landing-ai-highlights-description">
                  Shows the two strongest live AI signals from valid categorized products only.
                </p>
                <div className="grid sm:grid-cols-2 gap-3">
                  <div className="admin-intel-card" data-testid="landing-demand-velocity-card">
                    <p className="text-[10px] font-semibold text-[#374151] uppercase tracking-wide mb-2">
                      <span className="inline-block w-2 h-2 rounded-full bg-[#2563eb] mr-1.5 align-middle"></span>
                      Demand Velocity
                    </p>
                    <p className="text-[11px] text-[#6B7280] mb-2" data-testid="landing-demand-velocity-description">
                      Shows products whose stock is dropping faster than normal.
                    </p>
                    <div className="space-y-2">
                      {landingVelocity.map((item, i) => {
                        const base = Math.max(landingVelocity[0]?.velocity_per_hour || 1, 1);
                        const pct = Math.min(100, Math.round((item.velocity_per_hour / base) * 100));
                        const display = resolveProductDisplay(item.product_code);
                        return (
                          <div key={`${item.product_code}-${i}`} data-testid={`landing-demand-velocity-row-${i}`}>
                            <div className="flex justify-between items-center text-xs mb-0.5">
                              <div className="truncate max-w-[68%]" data-testid={`landing-demand-velocity-product-${i}`}>
                                <p className="text-[#374151] font-medium truncate leading-tight">{display.primary}</p>
                                {display.secondary && <p className="text-[10px] text-[#9CA3AF] truncate leading-tight">{display.secondary}</p>}
                              </div>
                              <span className="number-font text-[#2563eb] font-medium">{formatQty(item.velocity_per_hour)}/h</span>
                            </div>
                            <div className="intel-bar-track"><div className="intel-bar-fill" style={{ width: `${pct}%`, backgroundColor: '#2563eb' }}></div></div>
                          </div>
                        );
                      })}
                      {landingVelocity.length === 0 && (
                        <div data-testid="landing-demand-velocity-product-0">
                          <p className="text-[10px] text-[#9CA3AF]">No velocity spikes yet</p>
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="admin-intel-card" data-testid="landing-stock-shock-card">
                    <p className="text-[10px] font-semibold text-[#374151] uppercase tracking-wide mb-2">
                      <span className="inline-block w-2 h-2 rounded-full bg-[#E63946] mr-1.5 align-middle"></span>
                      Stock Shock Watch
                    </p>
                    <p className="text-[11px] text-[#6B7280] mb-2" data-testid="landing-stock-shock-description">
                      Flags sudden stock drops that may need investigation.
                    </p>
                    <div className="space-y-2">
                      {landingShocks.map((item, i) => {
                        const base = Math.max(landingShocks[0]?.drop_pct || 1, 1);
                        const pct = Math.min(100, Math.round((item.drop_pct / base) * 100));
                        const display = resolveProductDisplay(item.product_code);
                        return (
                          <div key={`${item.product_code}-${i}`} data-testid={`landing-stock-shock-row-${i}`}>
                            <div className="flex justify-between items-center text-xs mb-0.5">
                              <div className="truncate max-w-[68%]" data-testid={`landing-stock-shock-product-${i}`}>
                                <p className="text-[#374151] font-medium truncate leading-tight">{display.primary}</p>
                                {display.secondary && <p className="text-[10px] text-[#9CA3AF] truncate leading-tight">{display.secondary}</p>}
                              </div>
                              <span className="number-font text-[#E63946] font-medium">-{item.drop_pct}%</span>
                            </div>
                            <div className="intel-bar-track"><div className="intel-bar-fill" style={{ width: `${pct}%`, backgroundColor: '#E63946' }}></div></div>
                          </div>
                        );
                      })}
                      {landingShocks.length === 0 && (
                        <div data-testid="landing-stock-shock-product-0">
                          <p className="text-[10px] text-[#9CA3AF]">No stock shocks detected</p>
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                <div className="mt-3 flex justify-end">
                  <Button
                    data-testid="landing-open-pulse-hq-button"
                    onClick={() => navigate('/master-ai')}
                    size="sm"
                    className="bg-[#111827] hover:bg-[#1f2937] text-white"
                  >
                    Open Pulse HQ
                  </Button>
                </div>
              </div>
            )}
          </div>
        )}
        {shouldShowInsights() && allStocks.length > 0 && (
          <div className="mb-4 bg-white rounded-lg border border-[#E5E7EB] p-3 sm:p-4">
            <div className="flex items-center justify-between mb-2 sm:mb-3">
              <div>
                <h3 className="text-xs font-semibold text-[#374151] flex items-center gap-2" data-testid="stock-insights-title">
                  <TrendingUp className="h-3.5 w-3.5 text-[#16a34a]" />
                  {isFirstOfMonth() ? "Monthly Insights" : "Weekly Insights"}
                </h3>
                <p className="text-[11px] text-[#6B7280] mt-1" data-testid="stock-insights-description">
                  Quick stock insight cards based on valid categorized products only.
                </p>
              </div>
              {userRole !== "admin" && (
                <span className="text-[10px] text-[#9CA3AF] uppercase tracking-wide">
                  {isFirstOfMonth() ? "1st of Month View" : "Monday View"}
                </span>
              )}
            </div>
            
            <div className="grid sm:grid-cols-2 gap-3">
              {/* Top Movers */}
              <div className="bg-[#F9FAFB] rounded-md p-3">
                <p className="text-[11px] text-[#16a34a] font-medium mb-2 flex items-center gap-1">
                  <TrendingUp className="h-3 w-3" /> Top Stock Levels
                </p>
                <p className="text-[11px] text-[#6B7280] mb-2" data-testid="top-stock-levels-description">
                  Shows the categorized products with the highest available stock right now.
                </p>
                <div className="space-y-1.5">
                  {topMovers.map((item, idx) => {
                    const display = resolveProductDisplay(item.product_code);
                    return (
                      <div key={idx} className="flex justify-between items-center text-xs">
                        <div className="truncate max-w-[68%]" data-testid={`top-mover-product-${idx}`}>
                          <p className="text-[#374151] font-medium truncate leading-tight">{display.primary}</p>
                          {display.secondary && (
                            <p className="text-[10px] text-[#9CA3AF] truncate leading-tight">{display.secondary}</p>
                          )}
                        </div>
                        <span className="number-font text-[#09090b]">{item.available_qty.toLocaleString()} SQM</span>
                      </div>
                    );
                  })}
                </div>
              </div>
              
              {/* Slow Movers */}
              <div className="bg-[#F9FAFB] rounded-md p-3">
                <p className="text-[11px] text-[#C79200] font-medium mb-2 flex items-center gap-1">
                  <TrendingDown className="h-3 w-3" /> Lowest Stock Levels
                </p>
                <p className="text-[11px] text-[#6B7280] mb-2" data-testid="lowest-stock-levels-description">
                  Shows the categorized products with the lowest available stock right now.
                </p>
                <div className="space-y-1.5">
                  {slowMovers.map((item, idx) => {
                    const display = resolveProductDisplay(item.product_code);
                    return (
                      <div key={idx} className="flex justify-between items-center text-xs">
                        <div className="truncate max-w-[68%]" data-testid={`slow-mover-product-${idx}`}>
                          <p className="text-[#374151] font-medium truncate leading-tight">{display.primary}</p>
                          {display.secondary && (
                            <p className="text-[10px] text-[#9CA3AF] truncate leading-tight">{display.secondary}</p>
                          )}
                        </div>
                        <span className="number-font text-[#09090b]">{item.available_qty.toLocaleString()} SQM</span>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Reorder Suggestions Panel - Admin Only */}
        {userRole === "admin" && reorderItems.length > 0 && (
          <div className="mb-4 bg-white rounded-lg border border-[#E5E7EB] p-3 sm:p-4">
            <div className="flex items-center justify-between mb-2">
              <h3 className="text-xs font-semibold text-[#C79200] flex items-center gap-2">
                <ShoppingCart className="h-3.5 w-3.5" />
                Reorder Suggestions
              </h3>
              <span className="text-[10px] text-[#9CA3AF] uppercase tracking-wide">
                Below {REORDER_THRESHOLD} SQM
              </span>
            </div>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-2">
              {reorderItems.map((item, idx) => {
                const display = resolveProductDisplay(item.product_code);
                return (
                  <div key={idx} className="bg-[#F9FAFB] rounded-md px-3 py-2 flex justify-between items-center overflow-hidden">
                    <div className="truncate flex-1 mr-2" data-testid={`reorder-product-${idx}`}>
                      <p className="text-[#374151] text-xs font-medium truncate leading-tight">{display.primary}</p>
                      {display.secondary && (
                        <p className="text-[10px] text-[#9CA3AF] truncate leading-tight">{display.secondary}</p>
                      )}
                    </div>
                    <span className="number-font text-xs text-[#C79200] font-medium whitespace-nowrap">{item.available_qty} SQM</span>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        <div className="mb-4">
          {/* Recently Viewed strip (staff only) */}
          {!isAdmin && recentlyViewed.length > 0 && (
            <div className="mb-3" data-testid="recently-viewed">
              <button
                onClick={() => setShowRecent(!showRecent)}
                className="flex items-center gap-1.5 text-[11px] text-[#6B7280] font-medium mb-1.5 hover:text-[#374151] transition-colors"
                data-testid="toggle-recent"
              >
                <Clock className="h-3 w-3" />
                Recently Viewed ({recentlyViewed.length})
                <ChevronUp className={`h-3 w-3 transition-transform ${showRecent ? '' : 'rotate-180'}`} />
              </button>
              {showRecent && (
                <div className="flex gap-2 overflow-x-auto pb-1">
                  {recentlyViewed.map(code => {
                    const stock = allStocks.find(s => s.product_code === code);
                    if (!stock) return null;
                    const afterRes = Math.max(0, stock.available_qty - stock.reserved_qty);
                    return (
                      <button
                        key={code}
                        onClick={() => { setSearchQuery(code); fetchStocks(code); }}
                        className="recent-chip"
                        data-testid={`recent-${code}`}
                      >
                        <span className="font-medium text-[#09090b]">{code}</span>
                        <span className="text-[#6B7280]">{afterRes} SQM</span>
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          )}

          <div className="relative mb-3 flex gap-2">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-[#9CA3AF]" />
              <Input
                data-testid="stock-search-input"
                type="text"
                placeholder="Search by product code or say it..."
                value={searchQuery}
                onChange={handleSearch}
                className="pl-10 h-12 text-sm border border-[#E5E7EB] focus-visible:ring-[#E63946] focus-visible:ring-offset-1 rounded-lg bg-white"
              />
            </div>
            <Button
              onClick={startVoiceSearch}
              disabled={isListening}
              className={`h-12 w-12 rounded-lg ${isListening ? 'bg-[#E63946] hover:bg-[#E63946]/90 animate-pulse' : 'bg-[#E63946] hover:bg-[#E63946]/90'}`}
              data-testid="voice-search-button"
            >
              {isListening ? <MicOff className="h-5 w-5" /> : <Mic className="h-5 w-5" />}
            </Button>
          </div>

          {/* Action Buttons */}
          <div className="flex gap-2 mb-3 flex-wrap items-center">
            {/* Sort dropdown */}
            <Select value={sortBy} onValueChange={setSortBy}>
              <SelectTrigger className="w-[130px] sm:w-[140px] h-9 text-xs" data-testid="sort-select">
                <ArrowUpDown className="h-3 w-3 mr-1" />
                <SelectValue placeholder="Sort by" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="name">Name (A-Z)</SelectItem>
                <SelectItem value="qty-high">Qty (High → Low)</SelectItem>
                <SelectItem value="qty-low">Low Stock First</SelectItem>
                <SelectItem value="category">Category</SelectItem>
              </SelectContent>
            </Select>
            
            {/* Export button - Admin only */}
            {userRole === "admin" && (
              <div className="flex items-center gap-1" data-testid="stock-export-controls">
                <Button
                  data-testid="export-csv-button"
                  onClick={() => handleExport('csv')}
                  variant="outline"
                  size="sm"
                  className="text-[#6B7280] border-[#D1D5DB] hover:bg-[#F3F4F6]"
                >
                  <Download className="h-4 w-4 sm:mr-2" />
                  <span className="hidden sm:inline">CSV</span>
                </Button>
                <Button
                  data-testid="export-excel-button"
                  onClick={() => handleExport('xlsx')}
                  variant="outline"
                  size="sm"
                  className="text-[#6B7280] border-[#D1D5DB] hover:bg-[#F3F4F6]"
                >
                  <Download className="h-4 w-4 sm:mr-2" />
                  <span className="hidden sm:inline">Excel</span>
                </Button>
              </div>
            )}
            
            {/* In Stock Only filter */}
            <Button
              onClick={toggleInStockOnly}
              variant={showInStockOnly ? "default" : "outline"}
              size="sm"
              className={showInStockOnly
                ? "bg-[#16a34a] hover:bg-[#16a34a]/90 text-white"
                : "text-[#16a34a] border-[#16a34a] hover:bg-[#f0fdf4]"
              }
            >
              <CheckCircle className="h-4 w-4 sm:mr-2" />
              <span className="hidden sm:inline">In Stock</span>
            </Button>
            <Button
              onClick={showLowStockOnly}
              variant={selectedCategory === "Low Stock" ? "default" : "outline"}
              size="sm"
              title="Items with less than 100 SQM available"
              className={selectedCategory === "Low Stock" 
                ? "bg-[#ca8a04] hover:bg-[#ca8a04]/90 text-white"
                : "text-[#ca8a04] border-[#ca8a04] hover:bg-[#fef3c7]"
              }
              data-testid="low-stock-filter-btn"
            >
              <AlertCircle className="h-4 w-4 mr-2" />
              Low Stock ({getCategoryLowStockCount()})
              <span className="hidden sm:inline text-[10px] opacity-70 ml-1">(&lt;100 SQM)</span>
            </Button>
            <Button
              onClick={showFavoritesOnly}
              variant={selectedCategory === "Favorites" ? "default" : "outline"}
              size="sm"
              className={selectedCategory === "Favorites"
                ? "bg-[#f59e0b] hover:bg-[#f59e0b]/90 text-white"
                : "text-[#f59e0b] border-[#f59e0b] hover:bg-[#fef3c7]"
              }
            >
              <Star className="h-4 w-4 mr-2" />
              Favorites ({favorites.length})
            </Button>
          </div>

          
          {/* Category Filter Pills */}
          <div className="relative category-scroll-wrapper">
            <div className="flex items-center gap-1 overflow-x-auto sm:overflow-visible sm:flex-wrap pb-2 -mx-3 px-3 sm:mx-0 sm:px-0">
              <Filter className="h-4 w-4 text-[#9CA3AF] flex-shrink-0 mr-1" />
              {categoryCounts.map((category) => (
                <button
                  key={category.name}
                  onClick={() => handleCategorySelect(category.name)}
                  data-testid={`category-${category.name.toLowerCase().replace(/\s+/g, '-')}`}
                  className={`category-pill ${selectedCategory === category.name ? 'active' : ''} ${SECONDARY_CATEGORIES.has(category.name) ? 'secondary' : ''}`}
                >
                  {CATEGORY_DISPLAY[category.name] || category.name}
                  <span className="pill-count">{category.count}</span>
                </button>
              ))}
              <button
                onClick={() => setShowCategoryLegend(prev => !prev)}
                data-testid="category-legend-toggle"
                className="flex-shrink-0 ml-1 p-1 rounded-full hover:bg-gray-200 transition-colors"
                title="Category color guide"
              >
                <HelpCircle className="h-4 w-4 text-[#9CA3AF]" />
              </button>
            </div>

            {/* Category Legend Overlay */}
            {showCategoryLegend && (
              <div
                data-testid="category-legend-overlay"
                className="absolute right-0 top-full mt-1 z-50 bg-white rounded-lg shadow-lg border border-gray-200 p-3 w-56 animate-in fade-in slide-in-from-top-1 duration-200"
              >
                <div className="flex items-center justify-between mb-2">
                  <span className="text-xs font-semibold text-gray-700 uppercase tracking-wide">Color Guide</span>
                  <button onClick={() => setShowCategoryLegend(false)} className="p-0.5 rounded hover:bg-gray-100">
                    <X className="h-3 w-3 text-gray-400" />
                  </button>
                </div>
                <div className="space-y-1.5">
                  {[
                    { name: "Carpet Tiles", color: "#DC2626" },
                    { name: "Broadloom", color: "#2563EB" },
                    { name: "SPC", color: "#16a34a" },
                    { name: "LVT", color: "#7c3aed" },
                    { name: "Victory", color: "#0891b2" },
                    { name: "Elements", color: "#1d4ed8" },
                    { name: "RAF", color: "#ca8a04" },
                    { name: "Tbond", color: "#ea580c" },
                    { name: "Discounted", color: "#9333ea" },
                    { name: "Other", color: "#9CA3AF" },
                  ].map(({ name, color }) => (
                    <div key={name} className="flex items-center gap-2">
                      <span className="w-3 h-3 rounded-sm flex-shrink-0" style={{ backgroundColor: color }} />
                      <span className="text-xs text-gray-600">{name}</span>
                    </div>
                  ))}
                </div>
                <p className="text-[10px] text-gray-400 mt-2 leading-tight">
                  Card left border color indicates product category
                </p>
              </div>
            )}
          </div>
        </div>

        {loading ? (
          <div className="space-y-4">
            {/* Skeleton Stats */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
              {[1, 2, 3, 4].map(i => (
                <div key={i} className="skeleton skeleton-stat"></div>
              ))}
            </div>
            {/* Skeleton Cards */}
            {[1, 2, 3, 4, 5].map(i => (
              <div key={i} className="skeleton skeleton-card"></div>
            ))}
          </div>
        ) : stocks.length === 0 ? (
          <div className="text-center py-20">
            <div className="w-20 h-20 bg-[#f1f5f9] rounded-full mx-auto flex items-center justify-center mb-4">
              <Search className="h-10 w-10 text-[#64748b]" />
            </div>
            <h3 className="heading-font text-2xl mb-2 text-[#09090b]">No Stock Items Found</h3>
            <p className="text-[#64748b]">
              {searchQuery ? "Try a different search term" : "Upload stock data from admin panel"}
            </p>
          </div>
        ) : (
          <div>
            <div className="mb-3 flex items-center justify-between">
              <p className="text-xs text-[#9CA3AF] font-medium">
                {selectedCategory && selectedCategory !== "All" ? (
                  <span>Inventory <span className="text-[#6B7280]">/</span> <span className="text-[#09090b] font-semibold">{selectedCategory}</span> <span className="text-[#6B7280]">—</span> </span>
                ) : null}
                Showing <span className="number-font text-[#09090b] text-sm">{stocks.length}</span> items
              </p>
            </div>
            <div className="space-y-2 animate-stagger pb-20 sm:pb-4">
              {getSortedStocks(stocks).map((stock, index) => {
                const status = getStockStatus(stock.available_qty, stock.reserved_qty);
                return (
                  <div
                    key={stock.id}
                    data-testid={`stock-item-${stock.product_code}`}
                    className="stock-card bg-white border-l-[5px] rounded-xl p-3 sm:p-4 animate-fade-in-up"
                    style={{ 
                      borderLeftColor: getCategoryBorderColor(stock.category),
                      animationDelay: `${Math.min(index * 30, 300)}ms`
                    }}
                  >
                    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-0.5">
                          <h3 className="heading-font text-[18px] leading-snug text-[#09090b] truncate">
                            {stock.description}
                          </h3>
                          <div className="flex items-center gap-0.5 flex-shrink-0">
                            {!isAdmin && (
                              <button
                                onClick={() => handleCopy(stock)}
                                className="hover:scale-110 transition-transform p-1"
                                data-testid={`copy-${stock.product_code}`}
                                title="Copy stock info"
                              >
                                <Copy className="h-4 w-4 text-[#9CA3AF] hover:text-[#374151]" />
                              </button>
                            )}
                            <button
                              onClick={() => { handleShare(stock); trackRecent(stock.product_code); }}
                              className="hover:scale-110 transition-transform p-1"
                              data-testid={`share-${stock.product_code}`}
                              title="Share via WhatsApp"
                            >
                              <Share2 className="h-4 w-4 text-[#25D366] hover:text-[#128C7E]" />
                            </button>
                            <button
                              onClick={() => { toggleFavorite(stock.product_code); trackRecent(stock.product_code); }}
                              className="hover:scale-110 transition-transform p-1"
                              data-testid={`favorite-${stock.product_code}`}
                            >
                              {favorites.includes(stock.product_code) ? (
                                <Star className="h-4 w-4 fill-[#f59e0b] text-[#f59e0b]" />
                              ) : (
                                <Star className="h-4 w-4 text-[#cbd5e1] hover:text-[#f59e0b]" />
                              )}
                            </button>
                          </div>
                        </div>
                        <p className="text-[13px] text-[#6B7280] leading-tight inline">
                          {stock.product_code}
                        </p>
                        {stock.category && (
                          <span className={`inline-block ml-2 px-2 py-0.5 text-[10px] font-medium rounded ${getCategoryClass(stock.category)}`}>
                            {stock.category}
                          </span>
                        )}
                        {/* Phase 1A: Health badge */}
                        {(() => {
                          const health = getHealthBadge(stock);
                          const pressure = stock.available_qty > 0 ? Math.round(stock.reserved_qty / stock.available_qty * 100) : 0;
                          const pillClass = health.label === 'Critical' ? 'health-critical' : health.label === 'Watch' ? 'health-watch' : 'health-healthy';
                          return (
                            <span className={`health-pill ${pillClass}`} data-testid={`health-${stock.product_code}`}>
                              {health.label}
                              {pressure > 70 && (
                                <span className="ml-1 opacity-80">{pressure > 100 ? '100%+' : `${pressure}%`}</span>
                              )}
                            </span>
                          );
                        })()}
                        {/* Divider between identity and metrics */}
                        <div className="card-divider mt-2 mb-1"></div>
                      </div>

                      <div className="flex items-start gap-2 sm:gap-4 flex-wrap">
                        <div className="metric-col">
                          <p className="metric-label">Available</p>
                          <p
                            className="metric-value"
                            style={{ color: status.color }}
                            data-testid={`available-qty-${stock.product_code}`}
                          >
                            {formatQty(stock.available_qty)}
                          </p>
                          <p className="metric-unit">{stock.unit}</p>
                        </div>

                        <div className="metric-col">
                          <p className="metric-label">Reserved</p>
                          <p
                            className="metric-value text-[#ca8a04]"
                            data-testid={`reserved-qty-${stock.product_code}`}
                          >
                            {formatQty(stock.reserved_qty)}
                          </p>
                          <p className="metric-unit">{stock.unit}</p>
                        </div>

                        <div className="metric-col">
                          <p className="metric-label">After Res.</p>
                          <p
                            className="metric-value text-[#16a34a]"
                            data-testid={`after-res-qty-${stock.product_code}`}
                          >
                            {formatQty(Math.max(0, stock.available_qty - stock.reserved_qty))}
                          </p>
                          <p className="metric-unit">{stock.unit}</p>
                        </div>

                        {(userRole === "admin" || userRole === "logistics") && appReservedSummary[stock.product_code] && (
                          <div className="metric-col">
                            <p className="metric-label" style={{ color: "#3B82F6" }}>App Rsvd</p>
                            <p
                              className="metric-value"
                              style={{ color: "#3B82F6" }}
                              data-testid={`app-reserved-${stock.product_code}`}
                            >
                              {formatQty(appReservedSummary[stock.product_code].total)}
                            </p>
                            <p className="metric-unit">{stock.unit}</p>
                            {/* #27 Over-Reserved Warning */}
                            {stock.available_qty > 0 && (appReservedSummary[stock.product_code].total / stock.available_qty) >= 0.8 && (
                              <span data-testid={`over-reserved-${stock.product_code}`} className="inline-block mt-1 px-1.5 py-0.5 bg-orange-100 text-orange-700 text-[9px] font-semibold rounded uppercase tracking-wide">
                                &gt;80% Rsvd
                              </span>
                            )}
                          </div>
                        )}

                        {(stock.dye_lots ?? []).length > 0 && stock.available_qty > 0 && (
                          <div className="batch-section min-w-[160px] sm:min-w-[200px]">
                            <p className="metric-label mb-1 flex items-center gap-1">
                              Batches ({(stock.dye_lots ?? []).length})
                              {(stock.dye_lots ?? []).length > 5 && <span className="text-[10px] text-[#6366F1] font-semibold ml-1">Split Lots</span>}
                            </p>
                            <div className="batch-scroll-wrapper">
                              <div className="batch-table-header batch-sticky-header">
                                <span>Batch</span>
                                <span className="w-12 text-right">Qty</span>
                              </div>
                              <div className={`${expandedBatches[stock.product_code] ? 'max-h-[300px]' : 'max-h-[104px]'} overflow-y-auto`}>
                                {(expandedBatches[stock.product_code] ? (stock.dye_lots ?? []) : (stock.dye_lots ?? []).slice(0, 4)).map((batch, idx) => {
                                  const parts = batch.split(':');
                                  const batchName = parts[0];
                                  const qty = parts[1] || '0';
                                  return (
                                    <div key={idx} className="batch-row">
                                      <span className="font-medium text-[#09090b] text-xs">{batchName}</span>
                                      <span className="number-font text-[#09090b] w-12 text-right text-xs">{formatQty(qty)}</span>
                                    </div>
                                  );
                                })}
                              </div>
                            </div>
                            {(stock.dye_lots ?? []).length > 4 && (
                              <button 
                                onClick={() => toggleBatchExpand(stock.product_code)}
                                className="batch-expand-btn text-[10px] text-[#E63946] hover:text-[#E63946]/70 text-center w-full mt-1 font-medium"
                                data-testid={`batch-expand-${stock.product_code}`}
                              >
                                {expandedBatches[stock.product_code] ? 'Show less' : `+${(stock.dye_lots ?? []).length - 4} more`}
                              </button>
                            )}
                          </div>
                        )}
                      </div>
                    </div>

                    {/* #16 Quick Reserve Button */}
                    <div className="px-3 pb-2">
                      <button
                        data-testid={`quick-reserve-${stock.product_code}`}
                        onClick={() => navigate(`/reservations?product=${stock.product_code}`)}
                        className="w-full text-xs font-semibold py-2 px-3 rounded-lg bg-[#EFF6FF] border border-[#BFDBFE] text-[#2563EB] hover:bg-[#DBEAFE] transition-colors flex items-center justify-center gap-1.5"
                      >
                        <ClipboardList className="h-3.5 w-3.5" />
                        Quick Reserve
                      </button>
                    </div>

                  </div>
                );
              })}
            </div>
          </div>
        )}
      </main>

      {/* Category Quick Jump FAB (staff only) */}
      {!isAdmin && (
        <>
          <button
            onClick={() => setShowQuickJump(!showQuickJump)}
            className="quick-jump-fab"
            data-testid="quick-jump-fab"
            title="Jump to category"
          >
            {showQuickJump ? <X className="h-5 w-5" /> : <Layers className="h-5 w-5" />}
          </button>
          {showQuickJump && (
            <div className="quick-jump-overlay" data-testid="quick-jump-overlay">
              <div className="quick-jump-panel">
                <div className="flex items-center justify-between mb-3">
                  <h4 className="text-sm font-semibold text-[#09090b]">Jump to Category</h4>
                  <button onClick={() => setShowQuickJump(false)} className="p-1 hover:bg-[#F3F4F6] rounded">
                    <X className="h-4 w-4 text-[#6B7280]" />
                  </button>
                </div>
                <div className="space-y-1">
                  {categoryCounts.map((cat) => (
                    <button
                      key={cat.name}
                      onClick={() => { handleCategorySelect(cat.name); setShowQuickJump(false); }}
                      data-testid={`jump-${cat.name.toLowerCase().replace(/\s+/g, '-')}`}
                      className={`quick-jump-item ${selectedCategory === cat.name ? 'active' : ''}`}
                    >
                      <span>{cat.name}</span>
                      <span className="number-font text-[#6B7280]">{cat.count}</span>
                    </button>
                  ))}
                </div>
              </div>
            </div>
          )}
        </>
      )}

      {/* Footer */}
      <footer className="mt-auto bg-[#09090b] text-white py-3 sm:py-4">
        <div className="max-w-7xl mx-auto px-3 sm:px-4">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-1 sm:gap-2 text-[10px] sm:text-xs text-gray-400">
            <div className="flex items-center gap-2">
              <img src="/logo.jpeg" alt="Red Magma" className="w-4 h-4 sm:w-5 sm:h-5 rounded" />
              <span>Red Magma General Trading LLC</span>
            </div>
            <div className="flex items-center gap-2 sm:gap-4">
              <span>Dubai, UAE</span>
              <span>•</span>
              <span>{new Date().getFullYear()}</span>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
